import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import * as motion from 'motion/react-m';

import {Headline} from 'UI/components/layout/Text';
import {WaypointCard} from 'UI/components/modules/WaypointCard';

import {useWaypointPreviewList} from './useWaypointPreviewList';
import {HeadphonesIcon, LockIcon} from 'Images/icons';

/** Per-item stagger delay (seconds) for the reveal animation of visible items. */
const STAGGER_DELAY_SEC = 0.08;

/** A single waypoint preview item. */
export interface WaypointPreview {
    /** The complete, untruncated short description. */
    description?: string;
    /** Link URL for unlocked waypoints. */
    href?: string;
    /** Unique ID. */
    id: string;
    /** Alt text for the waypoint's main image. */
    imageAlt?: string;
    /** URL of the waypoint's main image. */
    imageUrl?: string;
    /** Whether this waypoint is unlocked (user owns the walk). */
    isUnlocked: boolean;
    /** The waypoint number. */
    number: number;
    /** The waypoint title. */
    title: string;
}

/**
 * Renders a single waypoint row.
 *
 * @param wp     The waypoint preview item.
 * @param testId The list's `testId`, passed through to the row for its own `data-cy`.
 * @returns The row's JSX content.
 */
const renderWaypointItem = (wp: WaypointPreview, testId: string) => (
    <WaypointCard
        cornerIcon={wp.isUnlocked
            ? <HeadphonesIcon height={14} width={14} />
            : <LockIcon height={14} width={14} />}
        description={wp.description}
        dimmed={!wp.isUnlocked}
        href={wp.href}
        imageAlt={wp.imageAlt}
        imageUrl={wp.imageUrl}
        number={wp.number}
        testId={testId}
        title={wp.title}
    />
);

/**
 * The props for the `WaypointPreviewList` component.
 * Configures a list of waypoints, some unlocked and the rest locked.
 */
interface ComponentProps {
    /** The section heading. */
    heading: string;
    /** The remaining locked count label (e.g. "9 More Locked Locations"). */
    lockedCountLabel?: string;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'WaypointPreviewList'
     */
    testId?: string;
    /** Array of waypoint preview items. */
    waypoints: WaypointPreview[];
}

/**
 * A preview list of walk waypoints. Shows the first few unlocked/locked items and
 * a summary row for remaining locked waypoints. All waypoints are always rendered in the
 * DOM — the collapsed ones are only hidden visually (via CSS) so search engines can still
 * crawl and index the full list, not just the initially visible entries.
 * Matches the Stitch design's "Walk Waypoints" / "The Journey" section.
 *
 * @param props                  The component props.
 * @param props.heading          The section heading text.
 * @param props.lockedCountLabel Optional label for remaining locked count.
 * @param props.testId           An optional test ID used for automation selectors. Defaults to `'WaypointPreviewList'`.
 * @param props.waypoints        Array of waypoint preview items.
 * @returns A JSX element rendering the WaypointPreviewList.
 *
 * @example
 * ```tsx
 * <WaypointPreviewList
 *     heading="Walk Waypoints"
 *     waypoints={waypoints}
 *     lockedCountLabel="9 More Locked Locations"
 * />
 * ```
 */
const WaypointPreviewList = ({
    heading,
    lockedCountLabel,
    testId = 'WaypointPreviewList',
    waypoints
}: ComponentProps) => {
    const {
        expanded, hiddenGroupId, hiddenWaypoints, setExpanded, t, visibleWaypoints
    } = useWaypointPreviewList(waypoints, testId);

    return (
        <Wrapper data-cy={testId}>
            <SectionHeading $size="headline" as="h2">{heading}</SectionHeading>
            <List>
                {visibleWaypoints.map((wp, index) => (
                    <ItemSlot key={wp.id} $spaced={index > 0}>
                        <motion.div
                            initial={{
                                opacity: 0,
                                y: 24
                            }}
                            transition={{
                                delay: index * STAGGER_DELAY_SEC,
                                duration: 0.4,
                                ease: 'easeOut'
                            }}
                            viewport={{
                                amount: 0.3,
                                once: true
                            }}
                            whileInView={{
                                opacity: 1,
                                y: 0
                            }}
                        >
                            {renderWaypointItem(wp, testId)}
                        </motion.div>
                    </ItemSlot>
                ))}
                {hiddenWaypoints.length > 0 && (
                    <HiddenGroup
                        animate={{
                            height: expanded ? 'auto' : 0,
                            marginTop: expanded ? spacing(3) : '0rem',
                            opacity: expanded ? 1 : 0
                        }}
                        aria-hidden={!expanded}
                        id={hiddenGroupId}
                        inert={!expanded}
                        initial={false}
                        transition={{
                            duration: 0.35,
                            ease: 'easeInOut'
                        }}
                    >
                        {hiddenWaypoints.map((wp, index) => (
                            <ItemSlot key={wp.id} $spaced={index > 0}>
                                {renderWaypointItem(wp, testId)}
                            </ItemSlot>
                        ))}
                    </HiddenGroup>
                )}
            </List>
            {!expanded && hiddenWaypoints.length > 0 && (
                <ExpandButton
                    aria-controls={hiddenGroupId}
                    aria-expanded={expanded}
                    data-cy={`${testId}-remaining`}
                    type="button"
                    onClick={() => setExpanded(true)}
                >
                    {lockedCountLabel ?? `${hiddenWaypoints.length} ${t('walkDetail.moreLockedLocations')}`}
                </ExpandButton>
            )}
        </Wrapper>
    );
};

WaypointPreviewList.displayName = 'WaypointPreviewList';

export {WaypointPreviewList};

// ── Styled ──────────────────────────────────────────────────

const Wrapper = styled.section`
`;

const SectionHeading = styled(Headline)`
    color: ${({theme}) => theme.colors.primaryDefault};
    margin-bottom: ${spacing(6)};
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
`;

/**
 * Spaces an item apart from its previous sibling within the same group (visible items, or the
 * hidden group). The gap between the visible group and the hidden group is instead controlled
 * by `HiddenGroup`'s own margin — see below.
 *
 * @param props         The styled-component props.
 * @param props.$spaced Whether to add top margin from the previous sibling.
 * @returns The computed `margin-top` CSS value.
 */
const ItemSlot = styled.div<{$spaced: boolean}>`
    margin-top: ${({$spaced}) => ($spaced ? spacing(3) : 0)};
`;

/**
 * Groups the waypoints beyond `PREVIEW_LIMIT`. Always rendered in the DOM — so search engines
 * can still crawl and index the full list — but collapsed to zero height/opacity and marked
 * `inert` (unfocusable, hidden from assistive tech) until expanded. Margin only applies once
 * expanded, so the collapsed group never reserves spacing before the "show more" button.
 *
 * Animates `height` to/from `'auto'` (via `motion`, which measures the real content height)
 * instead of transitioning to a fixed `max-height` guess — a fixed cap clips any group taller
 * than that guess, cutting off the last entries once several waypoints with long descriptions
 * are revealed.
 */
const HiddenGroup = styled(motion.div)`
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const ExpandButton = styled.button`
    appearance: none;
    background: ${({theme}) => theme.colors.surfaceHigh};
    border: none;
    border-radius: 9999px;
    color: ${({theme}) => theme.colors.inkSecondary};
    cursor: pointer;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
    font-weight: 700;
    margin-top: ${spacing(4)};
    padding: ${spacing(3)} 0;
    text-align: center;
    width: 100%;

    &:hover {
        background: ${({theme}) => theme.colors.surfaceMid};
    }
`;